import heic2any from 'heic2any';
import * as ExifReader from 'exifreader';
import * as piexif from 'piexifjs';

export interface ConversionResult {
  file: File;
  wasConverted: boolean;
  originalFormat: string;
  metadata?: Record<string, unknown>;
  error?: string;
}

export interface ConversionOptions {
  quality: number; // 0.0 to 1.0, default 0.95
  format: 'JPEG' | 'PNG'; // default 'JPEG'
  preserveMetadata: boolean; // default true
}

const DEFAULT_OPTIONS: ConversionOptions = {
  quality: 0.95,
  format: 'JPEG',
  preserveMetadata: true,
};

/**
 * Detects if a file is in HEIC format
 */
export const isHeicFile = (file: File): boolean => {
  const heicMimeTypes = ['image/heic', 'image/heif'];
  const heicExtensions = ['.heic', '.heif', '.HEIC', '.HEIF'];
  
  // Check MIME type first
  if (heicMimeTypes.includes(file.type)) {
    return true;
  }
  
  // Check file extension as fallback
  const fileName = file.name.toLowerCase();
  return heicExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
};

/**
 * Extracts EXIF metadata from HEIC file using ExifReader
 */
const extractHeicMetadata = async (file: File): Promise<Record<string, unknown> | undefined> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const tags = ExifReader.load(arrayBuffer, { expanded: true });
    return tags as Record<string, unknown>;
  } catch (error) {
    console.warn('Failed to extract HEIC metadata:', error);
    return undefined;
  }
};

/**
 * Converts ExifReader metadata to piexifjs format
 */
const convertMetadataToPiexifFormat = (exifData: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!exifData) return undefined;
  
  try {
    const piexifData: Record<string, Record<string, unknown> | undefined> = {
      '0th': {},
      'Exif': {},
      'GPS': {},
      'Interop': {},
      '1st': {},
      'thumbnail': undefined
    };

    // Map ExifReader format to piexif format
    if (exifData.ifd0 && typeof exifData.ifd0 === 'object') {
      Object.entries(exifData.ifd0 as Record<string, unknown>).forEach(([, value]) => {
        if (value && typeof value === 'object' && 'id' in value && 'value' in value) {
          const item = value as { id: string; value?: unknown; description?: unknown };
          const target = piexifData['0th'];
          if (target && typeof target === 'object') {
            target[item.id] = item.value || item.description;
          }
        }
      });
    }

    if (exifData.exif && typeof exifData.exif === 'object') {
      Object.entries(exifData.exif as Record<string, unknown>).forEach(([, value]) => {
        if (value && typeof value === 'object' && 'id' in value && 'value' in value) {
          const item = value as { id: string; value?: unknown; description?: unknown };
          const target = piexifData['Exif'];
          if (target && typeof target === 'object') {
            target[item.id] = item.value || item.description;
          }
        }
      });
    }

    if (exifData.gps && typeof exifData.gps === 'object') {
      Object.entries(exifData.gps as Record<string, unknown>).forEach(([, value]) => {
        if (value && typeof value === 'object' && 'id' in value && 'value' in value) {
          const item = value as { id: string; value?: unknown; description?: unknown };
          const target = piexifData['GPS'];
          if (target && typeof target === 'object') {
            target[item.id] = item.value || item.description;
          }
        }
      });
    }

    return piexifData;
  } catch (error) {
    console.warn('Failed to convert metadata format:', error);
    return undefined;
  }
};

/**
 * Embeds metadata into JPEG using piexifjs
 */
const embedMetadataIntoJpeg = (jpegBlob: Blob, metadata: Record<string, unknown> | undefined): Promise<Blob> => {
  return new Promise((resolve) => {
    if (!metadata) {
      resolve(jpegBlob);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jpegData = reader.result as string;
          const exifBytes = piexif.dump(metadata);
          const jpegWithExif = piexif.insert(exifBytes, jpegData);
          
          // Convert base64 back to blob
          const byteCharacters = atob(jpegWithExif.split(',')[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const resultBlob = new Blob([byteArray], { type: 'image/jpeg' });
          resolve(resultBlob);
        } catch (error) {
          console.warn('Failed to embed metadata, returning JPEG without metadata:', error);
          resolve(jpegBlob);
        }
      };
      reader.onerror = () => {
        console.warn('Failed to read JPEG for metadata embedding');
        resolve(jpegBlob);
      };
      reader.readAsDataURL(jpegBlob);
    } catch (error) {
      console.warn('Failed to process metadata embedding:', error);
      resolve(jpegBlob);
    }
  });
};

/**
 * Generates new filename with JPEG extension
 */
const generateJpegFilename = (originalName: string): string => {
  const heicExtensions = /\.(heic|heif)$/i;
  return originalName.replace(heicExtensions, '.jpg');
};

/**
 * Converts HEIC file to JPEG with metadata preservation
 */
export const convertHeicToJpeg = async (
  file: File,
  options: Partial<ConversionOptions> = {}
): Promise<ConversionResult> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  try {
    // Extract metadata first (before conversion)
    let metadata: Record<string, unknown> | undefined = undefined;
    if (opts.preserveMetadata) {
      const exifData = await extractHeicMetadata(file);
      metadata = convertMetadataToPiexifFormat(exifData);
    }

    // Convert HEIC to JPEG using heic2any
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: opts.quality,
    }) as Blob;

    // Embed metadata into converted JPEG
    const finalBlob = opts.preserveMetadata && metadata 
      ? await embedMetadataIntoJpeg(convertedBlob, metadata)
      : convertedBlob;

    // Create new File object with preserved filename (but .jpg extension)
    const newFileName = generateJpegFilename(file.name);
    const convertedFile = new File([finalBlob], newFileName, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });

    return {
      file: convertedFile,
      wasConverted: true,
      originalFormat: 'HEIC',
      metadata: metadata || undefined,
    };

  } catch (error) {
    console.error('HEIC conversion failed:', error);
    return {
      file: file,
      wasConverted: false,
      originalFormat: 'HEIC',
      error: error instanceof Error ? error.message : 'Unknown conversion error',
    };
  }
};

/**
 * Main function: Converts HEIC to JPEG if needed, passes through other formats unchanged
 */
export const convertHeicToJpegWithMetadata = async (
  file: File,
  options: Partial<ConversionOptions> = {}
): Promise<File> => {
  // Check if file is HEIC format
  if (!isHeicFile(file)) {
    // Not a HEIC file, return unchanged
    return file;
  }

  console.log(`Converting HEIC file: ${file.name}`);
  
  // Convert HEIC to JPEG
  const result = await convertHeicToJpeg(file, options);
  
  if (result.wasConverted) {
    console.log(`✅ Successfully converted ${file.name} → ${result.file.name}`);
    return result.file;
  } else {
    console.warn(`⚠️ Failed to convert ${file.name}, using original file. Error: ${result.error}`);
    return file; // Return original file as fallback
  }
};

/**
 * Batch conversion utility for multiple files
 */
export const convertMultipleFiles = async (
  files: File[],
  options: Partial<ConversionOptions> = {},
  onProgress?: (progress: number, currentFile: string, convertedCount: number) => void
): Promise<File[]> => {
  const convertedFiles: File[] = [];
  let convertedCount = 0;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    if (onProgress) {
      onProgress((i / files.length) * 100, file.name, convertedCount);
    }
    
    const convertedFile = await convertHeicToJpegWithMetadata(file, options);
    convertedFiles.push(convertedFile);
    
    if (isHeicFile(file)) {
      convertedCount++;
    }
  }
  
  if (onProgress) {
    onProgress(100, 'Complete', convertedCount);
  }
  
  console.log(`🎉 Conversion complete: ${convertedCount} HEIC files converted to JPEG`);
  return convertedFiles;
};

/**
 * Utility to check if any files in an array are HEIC
 */
export const hasHeicFiles = (files: File[]): boolean => {
  return files.some(file => isHeicFile(file));
};

/**
 * Get conversion statistics for a file array
 */
export const getConversionStats = (files: File[]) => {
  const heicFiles = files.filter(file => isHeicFile(file));
  const jpegFiles = files.filter(file => file.type === 'image/jpeg');
  const otherFiles = files.filter(file => !isHeicFile(file) && file.type !== 'image/jpeg');
  
  return {
    total: files.length,
    heic: heicFiles.length,
    jpeg: jpegFiles.length,
    other: otherFiles.length,
    needsConversion: heicFiles.length > 0,
  };
}; 